import React from 'react';
import { formatDate } from '../../../../util/util';
import { IBokbiStatistics } from '../../../../store/useBokbiHistory';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  ResponsiveContainer,
  Bar,
} from 'recharts';

const BokbiBarChart = ({
  bokbiStatistics,
}: {
  bokbiStatistics: IBokbiStatistics[];
}) => {
  const data = bokbiStatistics
    .map(item => {
      const originalUse = Number(item.amount.ORDER_PAY) || 0;
      const originalCharge = Number(item.amount.CHARGE) || 0;
      return {
        date: formatDate(item.createdAt)['yyyy.mm.dd'],
        rawDate: item.createdAt,
        // 원본 값 보관 (툴팁용)
        사용_raw: originalUse,
        충전_raw: originalCharge,
        // 막대(충전/사용)는 음수를 0으로 클램프(차트에서 음수 제거 위함)
        사용: Math.max(0, originalUse),
        충전: Math.max(0, originalCharge),
        잔액: item.balance,
      };
    })
    .sort((a, b) => new Date(a.rawDate).getTime() - new Date(b.rawDate).getTime());

  // 커스텀 툴팁
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const getDisplayValue = (entry: any) => {
        const row = entry?.payload || {};
        if (entry.dataKey === '사용') return Number(row['사용_raw'] ?? entry.value) || 0;
        if (entry.dataKey === '충전') return Number(row['충전_raw'] ?? entry.value) || 0;
        return entry.value; // 잔액은 그대로
      };
      return (
        <div className="!bg-white !p-4 !rounded-lg !shadow-lg !border !border-gray-200">
          <p className="!text-sm !font-medium !text-gray-900 !mb-2">{label}</p>
          {payload.map((entry: any, index: number) => {
            const val = getDisplayValue(entry);
            return (
              <p key={index} className="!text-sm" style={{ color: entry.color }}>
                {entry.name}: {Number(val).toLocaleString()}원
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Y축 포맷터
  const formatYAxis = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(0)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={data}
        margin={{
          top: 5,
          right: 10,
          left: 10,
          bottom: 5,
        }}
      >
        <defs>
          <linearGradient id="colorCharge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.4}/>
          </linearGradient>
          <linearGradient id="colorUse" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#EF4444" stopOpacity={0.4}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis 
          dataKey="date" 
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }}
        />
        <YAxis
          tickFormatter={formatYAxis}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ paddingTop: '20px' }}
          iconType="rect"
          formatter={(value) => <span className="!text-sm !font-medium">{value}</span>}
        />
        <Bar 
          dataKey="충전" 
          barSize={30} 
          fill="url(#colorCharge)"
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="사용" 
          barSize={30} 
          fill="url(#colorUse)"
          radius={[4, 4, 0, 0]}
        />
        <Line 
          type="monotone" 
          dataKey="잔액" 
          stroke="#10B981" 
          strokeWidth={3}
          dot={{ fill: '#10B981', r: 4 }}
          activeDot={{ r: 6 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default BokbiBarChart;
